// LES EMOTES côté client (26/07) : l'état courant + la roue de choix + le dessin de la bulle.
//
// Trois principes, tous destinés à ce qu'une emote ne puisse JAMAIS gêner le jeu :
//   1. UNE SEULE emote à la fois, et elle s'arrête toute seule au bout de EMOTE_MS ;
//   2. l'état vit ICI, hors de la simulation — aucun tick, aucun hash, aucun message réseau ;
//   3. tout ce qui bouge est dérivé du TEMPS écoulé (fonction pure), donc rien ne peut rester
//      coincé : même si un appelant oublie de nettoyer, la frame revient au repos d'elle-même.
//
// Déclenchement : la touche E ouvre la roue, les touches 1 à 8 jouent directement l'emote
// correspondante (l'ordre du menu). Sur écran tactile, un bouton ouvre la même roue.
import { EMOTE_MS, EMOTES, emoteById, emoteFrame, type EmoteFrame } from '../shared/emotes'

/** L'emote en cours : son id et l'horloge murale de son départ. null = aucune. */
let courante: { id: string; debut: number } | null = null

/** Joue une emote (remplace celle en cours, s'il y en a une). Id inconnu = ignoré. */
export function playEmote(id: string): void {
  if (!emoteById(id)) return
  courante = { id, debut: performance.now() }
}

/** Arrête tout de suite l'emote en cours (mort du coureur, changement d'écran). */
export function stopEmote(): void {
  courante = null
}

/** L'emote en cours et son animation à cet instant, ou null. Purge l'emote expirée au passage. */
export function currentEmote(nowMs: number): { emoji: string; frame: EmoteFrame } | null {
  if (!courante) return null
  const elapsed = nowMs - courante.debut
  if (elapsed >= EMOTE_MS) { courante = null; return null }
  const e = emoteById(courante.id)
  if (!e) { courante = null; return null }
  return { emoji: e.emoji, frame: emoteFrame(e.move, elapsed) }
}

/**
 * Dessine la BULLE au-dessus de la tête, en espace MONDE (mêmes coordonnées que le héros).
 * `px`, `py` = coin haut-gauche de la case du coureur ; `ts` = côté de la case.
 */
export function drawEmoteBubble(ctx: CanvasRenderingContext2D, px: number, py: number, ts: number, nowMs: number): void {
  const cur = currentEmote(nowMs)
  if (!cur || cur.frame.bulle <= 0.01) return
  const { bulle, monte, pop } = cur.frame
  const cx = px + ts / 2
  const cy = py - ts * monte
  // Taille : une bulle d'un demi-héros, pas plus. Au-delà, elle mange la scène et masque l'invite
  // de creusage juste à côté (constaté à l'écran avec un rayon de 0,34 case).
  const r = ts * 0.25 * (0.62 + 0.38 * pop) // petit « pop » à l'apparition
  ctx.save()
  ctx.globalAlpha = bulle
  // la bulle : un rond clair et sa petite pointe vers la tête
  ctx.fillStyle = 'rgba(22,18,48,0.88)'
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill()
  ctx.strokeStyle = 'rgba(255,210,63,0.55)'
  ctx.lineWidth = Math.max(1, ts * 0.035)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(cx - r * 0.28, cy + r * 0.82)
  ctx.lineTo(cx, cy + r * 1.5)
  ctx.lineTo(cx + r * 0.06, cy + r * 0.86)
  ctx.closePath()
  ctx.fillStyle = 'rgba(22,18,48,0.88)'
  ctx.fill()
  // L'emoji REMPLIT la bulle. Taille fixe et généreuse : un glyphe emoji mesure environ sa taille
  // de police en largeur (vérifié dans le navigateur : 120 px de police -> boîte de 76×119), donc
  // 1,6 fois le rayon tient dans le rond sans déborder. Pas de mise à l'échelle « intelligente » :
  // une correction par measureText divisait la taille par trois selon la police de repli.
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `${(r * 1.6).toFixed(1)}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", system-ui, sans-serif`
  ctx.fillText(cur.emoji, cx, cy + r * 0.04)
  ctx.restore()
}

// --- la roue de choix ------------------------------------------------------------------------------
let roue: HTMLElement | null = null

/** Ferme la roue si elle est ouverte. */
export function closeEmoteWheel(): void {
  roue?.remove()
  roue = null
}

/** Vrai si la roue est ouverte (l'appelant peut alors ignorer les touches de jeu). */
export function emoteWheelOpen(): boolean {
  return roue !== null
}

/**
 * Ouvre la roue d'emotes : les huit emotes en cercle, chacune avec son numéro de raccourci.
 * Un clic joue l'emote et referme. Un clic à côté, ou Échap, referme sans rien jouer.
 */
export function openEmoteWheel(): void {
  if (roue) { closeEmoteWheel(); return } // deuxième appui sur E : on referme
  const fond = document.createElement('div')
  fond.style.cssText = 'position:fixed;inset:0;z-index:74;background:rgba(8,6,20,.55);display:flex;align-items:center;justify-content:center;font-family:ui-monospace,Menlo,monospace'
  const cercle = document.createElement('div')
  const R = 118
  cercle.style.cssText = `position:relative;width:${R * 2 + 92}px;height:${R * 2 + 92}px`
  const titre = document.createElement('div')
  titre.textContent = 'Choisis ton emote'
  titre.style.cssText = `position:absolute;left:0;right:0;top:50%;transform:translateY(-50%);text-align:center;color:#cbc4e0;font-size:13px;line-height:1.5;pointer-events:none`
  const aide = document.createElement('div')
  aide.textContent = 'touches 1 à 8'
  aide.style.cssText = 'position:absolute;left:0;right:0;top:calc(50% + 22px);text-align:center;color:#7d769e;font-size:11px;pointer-events:none'
  cercle.append(titre, aide)
  EMOTES.forEach((e, i) => {
    const a = (i / EMOTES.length) * Math.PI * 2 - Math.PI / 2
    const b = document.createElement('button')
    b.type = 'button'
    b.style.cssText = `position:absolute;left:${R + 46 + Math.cos(a) * R - 34}px;top:${R + 46 + Math.sin(a) * R - 34}px;width:68px;height:68px;border-radius:50%;border:1px solid #35305e;background:#1d1840;color:#f0e9d2;cursor:pointer;font-family:inherit;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px`
    const em = document.createElement('div')
    em.textContent = e.emoji
    em.style.cssText = 'font-size:26px;line-height:1'
    const nom = document.createElement('div')
    nom.textContent = `${i + 1} · ${e.name}`
    nom.style.cssText = 'font-size:9px;color:#9a93c0;white-space:nowrap'
    b.append(em, nom)
    b.addEventListener('click', () => { playEmote(e.id); closeEmoteWheel() })
    cercle.appendChild(b)
  })
  fond.appendChild(cercle)
  fond.addEventListener('click', (ev) => { if (ev.target === fond) closeEmoteWheel() })
  document.body.appendChild(fond)
  roue = fond
}

/**
 * Traite une touche pour les emotes. Renvoie true si la touche a été CONSOMMÉE (l'appelant ne doit
 * alors pas la traiter comme une commande de jeu).
 *   · E       : ouvre / referme la roue
 *   · 1 à 8   : joue directement l'emote correspondante (et referme la roue)
 *   · Échap   : referme la roue (seulement si elle est ouverte)
 */
export function handleEmoteKey(code: string): boolean {
  if (code === 'KeyE') { openEmoteWheel(); return true }
  const m = /^Digit([1-8])$/.exec(code)
  if (m) {
    const e = EMOTES[Number(m[1]) - 1]
    if (e) { playEmote(e.id); closeEmoteWheel(); return true }
  }
  if (code === 'Escape' && roue) { closeEmoteWheel(); return true }
  return false
}
